import React, { useState } from 'react'
import { api } from '../services/userService'
import { Button } from '../ui/button'
import { Label } from '../ui/label'

interface Props {
  className?: string
  variant?: 'dark' | 'light'
}

export const ContactSupportForm: React.FC<Props> = ({ className, variant = 'dark' }) => {
  const isDark = variant === 'dark'
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    setIsSubmitting(true)
    setError(null)

    try {
      await api.post('/users/contact', {
        subject: subject.trim() || undefined,
        message: message.trim(),
      })
      setSuccess(true)
      setSubject('')
      setMessage('')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div
        className={`rounded-lg p-4 text-center border ${
          isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-green-950 border-green-800'
        } ${className}`}
      >
        <p className={`font-medium ${isDark ? 'text-green-600' : 'text-green-300'}`}>
          Message sent!
        </p>
        <p className={`text-sm mt-1 ${isDark ? 'text-gray-600' : 'text-gray-500'}`}>
          We'll be in touch soon.
        </p>
        <button
          onClick={() => setSuccess(false)}
          className={`mt-3 text-sm underline ${
            isDark ? 'text-gray-600 hover:text-foreground' : 'text-gray-500 hover:text-gray-200'
          }`}
        >
          Send another message
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div className="space-y-2">
        <Label htmlFor="support-subject" className={isDark ? 'text-foreground' : 'text-gray-300'}>
          Subject{' '}
          <span className={`font-normal ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            (optional)
          </span>
        </Label>
        <input
          id="support-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Brief description of your issue"
          className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2 ${
            isDark
              ? 'border-foreground/10 bg-card text-foreground placeholder:text-gray-500 focus:ring-[#C0FD72]/50 focus:border-[#C0FD72]/50'
              : 'border-gray-700 bg-foreground text-gray-100 placeholder:text-gray-600 focus:ring-blue-500/30 focus:border-blue-600'
          }`}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="support-message" className={isDark ? 'text-foreground' : 'text-gray-300'}>
          Message
        </Label>
        <textarea
          id="support-message"
          required
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Describe your issue in detail..."
          className={`flex w-full rounded-md border px-3 py-2 text-sm focus:outline-hidden focus:ring-2 resize-none ${
            isDark
              ? 'border-foreground/10 bg-card text-foreground placeholder:text-gray-500 focus:ring-[#C0FD72]/50 focus:border-[#C0FD72]/50'
              : 'border-gray-700 bg-foreground text-gray-100 placeholder:text-gray-600 focus:ring-blue-500/30 focus:border-blue-600'
          }`}
        />
      </div>

      {error && <p className={`text-sm ${isDark ? 'text-red-600' : 'text-red-400'}`}>{error}</p>}

      <Button
        type="submit"
        disabled={isSubmitting || !message.trim()}
        className="bg-[#C0FD72] hover:bg-[#b0ef62] text-black font-semibold"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  )
}
